import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { kr } from '../lib/format';
import { PageHeader, Card, StatCard, Modal } from '../components/ui';
import { AlertCircle, Bell, Mail, PlayCircle, FileText } from 'lucide-react';

const Purring = () => {
  const [rader, setRader] = useState([]);
  const [bekreft, setBekreft] = useState(false);
  const [resultat, setResultat] = useState(null);

  const last = useCallback(() => {
    api.get('/purring/oversikt').then(r => setRader(r.data));
  }, []);
  useEffect(() => { last(); }, [last]);

  const purr = async (f) => {
    if (!window.confirm(`Send purring nivå ${(f.purrenivaa || 0) + 1} for faktura ${f.fakturanummer}?`)) return;
    try { await api.post(`/faktura/${f.id}/purring`, {}); last(); }
    catch (e) { alert(e.response?.data?.error || 'Feil'); }
  };

  const sendEpost = async (f) => {
    try {
      await api.post(`/faktura/${f.id}/send-epost`, {});
      alert(`E-post sendt til ${f.kunde_epost}`);
    } catch (e) { alert(e.response?.data?.error || 'Feil ved sending'); }
  };

  const autoKjor = async () => {
    try {
      const r = await api.post('/purring/auto-kjor', {});
      setResultat(r.data); setBekreft(false); last();
    } catch (e) { alert(e.response?.data?.error || 'Feil'); }
  };

  const sumForfalt = rader.reduce((s, f) => s + f.sum_ink_mva, 0);
  const antForslag = rader.filter(f => f.forslag_nivaa > 0).length;
  const antInkasso = rader.filter(f => f.purrenivaa >= 3).length;

  const nivaaTekst = (n) => ['—', 'Purring 1', 'Purring 2', 'Inkassovarsel'][n] || `Nivå ${n}`;
  const nivaaFarge = (n) => n >= 3 ? 'var(--theme-danger)' : n === 2 ? 'var(--theme-warning)' : n === 1 ? 'var(--theme-accent)' : 'var(--theme-text-secondary)';

  return (
    <div>
      <PageHeader title="Purring" subtitle="Forfalte fakturaer og automatisk purring" actions={
        <button className="btn-primary" onClick={() => setBekreft(true)} disabled={!antForslag}>
          <PlayCircle size={14} /> Auto-kjør purring ({antForslag})
        </button>
      } />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
        <StatCard label="Forfalte fakturaer" value={rader.length} color="var(--theme-warning)" icon={AlertCircle} />
        <StatCard label="Sum forfalt" value={kr(sumForfalt)} color="var(--theme-danger)" />
        <StatCard label="Foreslått purret" value={antForslag} color="var(--theme-accent)" icon={Bell} />
        <StatCard label="Til inkasso" value={antInkasso} color="var(--theme-danger)" />
      </div>

      <Card>
        <table className="fm-table">
          <thead><tr>
            <th>Faktura</th><th>Kunde</th><th>Forfall</th><th style={{ textAlign: 'right' }}>Dager</th>
            <th style={{ textAlign: 'right' }}>Sum</th><th style={{ textAlign: 'right' }}>Gebyr</th>
            <th style={{ textAlign: 'right' }}>Rente</th><th>Nivå</th><th>Foreslått</th><th></th>
          </tr></thead>
          <tbody>
            {rader.map(f => (
              <tr key={f.id}>
                <td>{f.fakturanummer}</td>
                <td>{f.kunde_navn}</td>
                <td>{f.forfall}</td>
                <td style={{ textAlign: 'right', color: f.dager_forfalt >= 28 ? 'var(--theme-danger)' : 'inherit' }}>
                  {f.dager_forfalt}
                </td>
                <td style={{ textAlign: 'right' }}>{kr(f.sum_ink_mva)}</td>
                <td style={{ textAlign: 'right' }}>{kr(f.purregebyr)}</td>
                <td style={{ textAlign: 'right' }}>{kr(f.forsinkelsesrente)}</td>
                <td><span style={{ color: nivaaFarge(f.purrenivaa) }}>{nivaaTekst(f.purrenivaa)}</span></td>
                <td>{f.forslag_nivaa > 0 && <strong style={{ color: nivaaFarge(f.forslag_nivaa) }}>{nivaaTekst(f.forslag_nivaa)}</strong>}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn-ghost" onClick={() => purr(f)} disabled={f.purrenivaa >= 3} title="Purr"><Bell size={13} /></button>
                    {f.kunde_epost && <button className="btn-ghost" onClick={() => sendEpost(f)} title="Send på e-post"><Mail size={13} /></button>}
                    <a className="btn-ghost" href={`/faktura/${f.id}`} title="Åpne"><FileText size={13} /></a>
                  </div>
                </td>
              </tr>
            ))}
            {!rader.length && <tr><td colSpan={10} className="fm-text-muted" style={{ textAlign: 'center', padding: 24 }}>
              Ingen forfalte fakturaer.
            </td></tr>}
          </tbody>
        </table>
      </Card>

      {bekreft && (
        <Modal open onClose={() => setBekreft(false)} title="Auto-kjør purring"
               footer={<><button className="btn-ghost" onClick={() => setBekreft(false)}>Avbryt</button>
                         <button className="btn-primary" onClick={autoKjor}>Kjør</button></>}>
          <p>Dette vil purre alle fakturaer automatisk etter regler:</p>
          <ul>
            <li>14+ dager: Purring 1 (gebyr 75 kr)</li>
            <li>28+ dager: Purring 2 (gebyr 145 kr)</li>
            <li>42+ dager: Inkassovarsel</li>
          </ul>
          <p>Forsinkelsesrente legges til (standard 12,5% p.a.).</p>
          <p><strong>{antForslag} fakturaer</strong> vil bli oppdatert.</p>
        </Modal>
      )}

      {resultat && (
        <Modal open onClose={() => setResultat(null)} title="Auto-purring fullført"
               footer={<button className="btn-primary" onClick={() => setResultat(null)}>OK</button>}>
          <p>{resultat.antall} fakturaer ble purret:</p>
          <ul>
            {resultat.purret.map(p => <li key={p.id}>#{p.fakturanummer} – {p.kunde} – {nivaaTekst(p.nivaa)}</li>)}
          </ul>
        </Modal>
      )}
    </div>
  );
};

export default Purring;
